import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Button,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Divider,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowBack, FileDownload, CheckCircle, Remove, ExpandMore, PictureAsPdf, FactCheck } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreInicial, saveQuadrimestre, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import FonteDadosSelector, { getFonteInicial, FonteDados } from '../../components/saude/FonteDadosSelector';
import HelpButton from '../../components/saude/HelpButton';
import BoaPraticaProgressoDialog from '../../components/saude/BoaPraticaProgressoDialog';
import { BOA_PRATICA_PROGRESSO, calcProgresso, ProgressoDescritor } from '../../data/boa-pratica-progresso';
import { focoStatusLabels, focoDataLabel, focoTituloSufixo, isFocoTipoDemanda } from '../../utils/saude-foco';

// Eixos que exibem o painel de pontuacao (Peso / Maxima / Atual / A conquistar) no modo foco.
// Passo manual e explicito por eixo — mesmo padrao de BOA_PRATICA_CLICAVEL em SaudeEixo.tsx.
const PAINEL_PONTUACAO_BOA_PRATICA = new Set<string>(['c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres', 'ecr-gestacao', 'ecr-ist', 'ecr-tuberculose']);

interface Paciente {
  co_seq_fat_cidadao_pec: number;
  nu_cpf_cidadao: string;
  nu_cns: string;
  no_cidadao: string;
  dt_nascimento: string;
  idade: number;
  sexo: string;
  ine: string;
  equipe: string;
  unidade: string;
  score_total: number;
  [key: string]: any;
}

interface Paginacao {
  page: number;
  limit: number;
  total: number;
  total_paginas: number;
}

interface EquipeInfo {
  ine: string;
  equipe: string;
  unidade: string;
  // Loaded data
  pacientes?: Paciente[];
  paginacao?: Paginacao;
  loading?: boolean;
  page?: number;
}

interface IndicatorCol {
  key: string;
  label: string;
  dateKey?: string;
}

const INDICATOR_COLUMNS: { [eixo: string]: IndicatorCol[] } = {
  'c1-mais-acesso': [],
  'c2-criancas': [
    { key: 'ind_a', label: '1a Consulta 30d' },
    { key: 'ind_b', label: '9+ Consultas' },
    { key: 'ind_c', label: 'Antropometria' },
    { key: 'ind_d', label: 'Visitas ACS' },
    { key: 'ind_e', label: 'Vacinas' },
  ],
  'c3-gestantes': [
    { key: 'ind_a', label: 'Inicio Precoce' },
    { key: 'ind_b', label: '7+ Consultas' },
    { key: 'ind_c', label: 'PA' },
    { key: 'ind_d', label: 'Antropometria' },
    { key: 'ind_e', label: 'Visitas ACS' },
    { key: 'ind_f', label: 'dTpa' },
    { key: 'ind_g', label: 'Exames 1oTri' },
    { key: 'ind_h', label: 'Exames 3oTri' },
    { key: 'ind_i', label: 'Cons. Puerp.' },
    { key: 'ind_j', label: 'Visita Puerp.' },
    { key: 'ind_k', label: 'Saude Bucal' },
  ],
  'c4-diabeticos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
    { key: 'ind_e', label: 'HbA1c', dateKey: 'dt_ultimo_hba1c' },
    { key: 'ind_f', label: 'Pe Diabetico', dateKey: 'dt_ultimo_pe_diabetico' },
  ],
  'c5-hipertensos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
  ],
  'c6-idosos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_c', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
    { key: 'ind_d', label: 'Influenza', dateKey: 'dt_ultima_influenza' },
  ],
  'c7-mulheres': [
    { key: 'ind_a', label: 'Citopatologico', dateKey: 'dt_ultima_citopat' },
    { key: 'ind_b', label: 'HPV', dateKey: 'dt_ultima_hpv' },
    { key: 'ind_c', label: 'Saude Sexual', dateKey: 'dt_ultima_saude_sexual' },
    { key: 'ind_d', label: 'Mamografia', dateKey: 'dt_ultima_mamografia' },
  ],
  'eapp-gestacao': [
    { key: 'ind_a', label: '3+ Consultas' },
    { key: 'ind_b', label: 'Testes IST' },
    { key: 'ind_c', label: 'dTpa' },
    { key: 'ind_d', label: '3+ PA' },
    { key: 'ind_e', label: 'Saude Bucal' },
  ],
  'eapp-diabetes-has': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'HbA1c', dateKey: 'dt_ultima_hba1c' },
  ],
  'eapp-ist': [
    { key: 'ind_a', label: 'HIV' },
    { key: 'ind_b', label: 'Sifilis' },
    { key: 'ind_c', label: 'Hep. B' },
    { key: 'ind_d', label: 'Hep. C' },
  ],
  'eapp-cancer': [
    { key: 'ind_a', label: 'Citopatologico', dateKey: 'dt_ultima_citopat' },
  ],
  'ecr-gestacao': [
    { key: 'ind_a', label: '3+ Consultas' },
    { key: 'ind_b', label: 'Testes IST' },
    { key: 'ind_c', label: 'dTpa' },
    { key: 'ind_d', label: '3+ PA' },
    { key: 'ind_e', label: 'Saude Bucal' },
  ],
  'ecr-ist': [
    { key: 'ind_a', label: 'HIV' },
    { key: 'ind_b', label: 'Sifilis' },
    { key: 'ind_c', label: 'Hep. B' },
    { key: 'ind_d', label: 'Hep. C' },
  ],
  'ecr-tuberculose': [
    { key: 'ind_a', label: 'Consulta' },
    { key: 'ind_b', label: 'Baciloscopia' },
    { key: 'ind_c', label: 'DOT' },
    { key: 'ind_d', label: 'Visita ACS' },
  ],
  'eapp-tuberculose': [
    { key: 'ind_a', label: 'Consulta Med/Enf' },
    { key: 'ind_b', label: 'Baciloscopia' },
    { key: 'ind_c', label: 'DOT' },
    { key: 'ind_d', label: 'Visita Domiciliar' },
  ],
  'nt30-vinculo': [
    { key: 'ind_cadastro', label: 'Cadastro (MICI)' },
    { key: 'ind_acompanhamento', label: 'Acompanhamento 12m' },
    { key: 'visitas_acs', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
  ],
  // eSB B1-B6: lista dos cidadaos contabilizados no indicador (data do atendimento)
  'b1-primeira-consulta': [
    { key: 'ind_a', label: '1a Consulta', dateKey: 'dt_evento' },
  ],
  'b2-tratamento-concluido': [
    { key: 'ind_b', label: 'Trat. Concluido', dateKey: 'dt_evento' },
  ],
  'b3-exodontia': [
    { key: 'ind_c', label: 'Exodontia', dateKey: 'dt_evento' },
  ],
  'b4-escovacao': [
    { key: 'ind_d', label: 'Escovacao', dateKey: 'dt_evento' },
  ],
  'b5-preventivos': [
    { key: 'ind_e', label: 'Preventivo', dateKey: 'dt_evento' },
  ],
  'b6-tra-art': [
    { key: 'ind_f', label: 'TRA/ART', dateKey: 'dt_evento' },
  ],
  // eMulti M1/M2: pessoas atendidas pela eMulti (nº de atendimentos/ações + data do último)
  'm1-media-atendimentos': [
    { key: 'ind_a', label: 'Atendimentos', dateKey: 'dt_evento' },
  ],
  'm2-acoes-interprofissionais': [
    { key: 'ind_b', label: 'Acoes Interprof.', dateKey: 'dt_evento' },
  ],
};

function maskCpf(cpf: string | null | undefined): string {
  if (!cpf) return '---';
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length < 5) return '***.***.***-**';
  const lastFive = cleaned.slice(-5);
  return `***.***${lastFive.length >= 5 ? '.' + lastFive.slice(0, 3) + '-' + lastFive.slice(3) : lastFive}`;
}

/**
 * Data do evento em DD/MM/AA. Faz o parse por COMPONENTES quando a string comeca com
 * YYYY-MM-DD (caso de todas as datas vindas do banco, inclusive "2026-06-30T00:00:00.000Z"):
 * o `new Date(...)` puro interpretaria a meia-noite como UTC e, num navegador em BRT (UTC-3),
 * exibiria o DIA ANTERIOR. Mesma convencao ja usada em formatDataNascimento/formatIdadeAnosMeses.
 */
function formatDate(dt: string | null | undefined): string {
  if (!dt) return '';
  const m = String(dt).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
  try {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
}

/**
 * Data de nascimento em DD/MM/AAAA (ano com 4 digitos). Parse por componentes (YYYY-MM-DD)
 * p/ evitar o desvio de fuso do `new Date('YYYY-MM-DD')` (UTC -> BRT poderia recuar 1 dia).
 */
function formatDataNascimento(dt?: string | null): string {
  const m = String(dt ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * Idade em "X anos e Y meses" (idade ATUAL, relativa a hoje) — usado no eixo de crianca (C2),
 * onde os meses importam. Faz o parse por componentes (YYYY-MM-DD) p/ evitar o desvio de fuso
 * do `new Date('2023-11-24')` (UTC -> BRT poderia recuar 1 dia). Singular/plural corretos.
 */
function formatIdadeAnosMeses(dt: string | null | undefined): string {
  if (!dt) return '-';
  const m = String(dt).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '-';
  const nasc = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(nasc.getTime())) return '-';
  const hoje = new Date();
  let anos = hoje.getFullYear() - nasc.getFullYear();
  let meses = hoje.getMonth() - nasc.getMonth();
  if (hoje.getDate() < nasc.getDate()) meses--;
  if (meses < 0) { anos--; meses += 12; }
  if (anos < 0) return '-';
  const partes: string[] = [];
  if (anos > 0) partes.push(`${anos} ${anos === 1 ? 'ano' : 'anos'}`);
  if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mês' : 'meses'}`);
  if (partes.length === 0) return 'menos de 1 mês';
  return partes.join(' e ');
}

const SaudeListaNominal: React.FC = () => {
  const { eixo } = useParams<{ eixo: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  // Eixo de crianca (C2): a coluna Idade mostra "X anos e Y meses" (idade atual); demais eixos so anos.
  const eixoEhCrianca = eixo === 'c2-criancas';

  const initialIne = searchParams.get('ine') || '';

  // Modo foco (boa pratica): quando ha ?indicador=A, a tela lista apenas os cidadaos
  // que faltam (0) ou entraram (1) naquela boa pratica, com colunas demograficas + CSV/PDF.
  const indicador = (searchParams.get('indicador') || '').toUpperCase();
  const focusMode = !!indicador;
  const statusInicial: 'faltam' | 'entraram' = searchParams.get('status') === 'entraram' ? 'entraram' : 'faltam';
  const [status, setStatus] = useState<'faltam' | 'entraram'>(statusInicial);

  const [agrupado, setAgrupado] = useState(!focusMode);
  const [equipesList, setEquipesList] = useState<EquipeInfo[]>([]);
  const [expandedIne, setExpandedIne] = useState<string>(initialIne);

  // Flat mode state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<Paciente[]>([]);
  const [eixoNome, setEixoNome] = useState('');
  const [paginacao, setPaginacao] = useState<Paginacao>({ page: 1, limit: 50, total: 0, total_paginas: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIne, _setSelectedIne] = useState<string>(initialIne);
  const [page, setPage] = useState(1);
  // "Buscar por nome/CPF/CNS". No modo foco (boa pratica) dos eixos eSF a busca e SERVER-SIDE
  // (varre todo o universo da boa pratica), com debounce e gatilho a partir de 3 caracteres.
  // Nos demais casos continua sendo um filtro client-side da pagina atual (searchName).
  const [searchName, setSearchName] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const buscaTimer = useRef<any>(null);
  // Filtro por microarea (server-side): reflete em Faltam/Entraram e nas exportacoes.
  // Diferente da "Buscar por nome" (client-side), dispara refetch quando tem exatamente 2 digitos.
  const [microInput, setMicroInput] = useState('');
  const [somenteIncompletos, setSomenteIncompletos] = useState(false);
  // Info da boa pratica (nome + numerador/denominador + peso) p/ rotulos do toggle Faltam/Entraram
  // e para o painel de pontuacao (Maxima/Atual/A conquistar).
  const [focoInfo, setFocoInfo] = useState<{ nome: string; numerador: number; denominador: number; peso: number; tipo?: string } | null>(null);
  // Totais reais das duas listas de foco (so p/ eixos de TAXA, ex.: b2, onde numerador NAO e
  // subconjunto do denominador e portanto faltam != den-num). Preenchido via 2 fetches limit=1.
  const [focoCountsTaxa, setFocoCountsTaxa] = useState<{ faltam: number; entraram: number } | null>(null);
  // Paciente selecionado para o dialogo de detalhe das boas praticas (progresso de cada uma).
  const [detalhe, setDetalhe] = useState<Paciente | null>(null);

  // Inicializa quadrimestre a partir da URL (?quadrimestre=Q2-2025) quando presente
  const quadFromUrl = searchParams.get('quadrimestre');
  const quadInicial = (() => {
    if (quadFromUrl) {
      const m = quadFromUrl.match(/^(Q[123])-(\d{4})$/i);
      if (m) return { quadrimestre: m[1].toUpperCase(), ano: parseInt(m[2], 10) };
    }
    return getQuadrimestreInicial();
  })();
  const [quadrimestre, setQuadrimestre] = useState(quadInicial.quadrimestre);
  const [ano, setAno] = useState(quadInicial.ano);
  const [fonte, setFonte] = useState<FonteDados>(getFonteInicial());
  const qParam = formatQuadrimestreParam(quadrimestre, ano);

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); setPage(1); saveQuadrimestre(q, a); };

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, novo: 'faltam' | 'entraram' | null) => {
    if (!novo || novo === status) return;
    setStatus(novo);
    setPage(1);
    const sp = new URLSearchParams(searchParams);
    sp.set('status', novo);
    navigate({ search: sp.toString() }, { replace: true });
  };

  const indicators = eixo ? INDICATOR_COLUMNS[eixo] || [] : [];

  // Eixos com filtro por microarea na lista nominal (espelha a allowlist do backend).
  const suportaMicro = focusMode && ['c6-idosos', 'c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c7-mulheres'].includes(eixo || '');
  // Valor efetivo enviado a API: aplica so com exatamente 2 digitos; vazio/1 digito -> todas.
  const microAplicada = suportaMicro && /^\d{2}$/.test(microInput) ? microInput : '';

  // Eixos eSF com busca server-side na boa pratica (espelha a allowlist BUSCA_FILTRO_EIXOS do backend).
  const suportaBusca = focusMode && ['c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres'].includes(eixo || '');
  // Valor efetivo enviado a API: so com 3+ caracteres (debounced). Abaixo disso -> universo completo.
  const buscaAplicada = suportaBusca && buscaDebounced.trim().length >= 3 ? buscaDebounced.trim() : '';

  // Progresso das boas praticas (fracao inline + dialogo de detalhe). Opt-in por eixo.
  const progresso = eixo ? BOA_PRATICA_PROGRESSO[eixo] : undefined;
  const temProgresso = !!progresso && progresso.length > 0;
  const progressoByKey: Record<string, ProgressoDescritor> = {};
  if (progresso) progresso.forEach((p) => { progressoByKey[p.key] = p; });

  // Rotulos do modo foco: "Faltam/Entraram" por boa pratica, "Espontaneos/Programados" no C1.
  const focoLabels = focoStatusLabels(eixo);

  // Eixos boas_praticas: numerador ⊆ denominador -> faltam = den - num, entraram = num (do resumo).
  // Eixos de TAXA (ex.: b2): usa os totais reais das listas de foco (focoCountsTaxa).
  const faltamCount = focoCountsTaxa ? focoCountsTaxa.faltam : (focoInfo ? Math.max(focoInfo.denominador - focoInfo.numerador, 0) : null);
  const entraramCount = focoCountsTaxa ? focoCountsTaxa.entraram : (focoInfo ? focoInfo.numerador : null);

  // Painel de pontuacao (visualizacao): cada paciente "vale" o peso da boa pratica.
  // Nao e o score oficial do eixo (que limita cada indicador ao proprio peso) — ver GUIA-LISTA-BOA-PRATICA.md.
  const mostrarPainelPontuacao = !!eixo && PAINEL_PONTUACAO_BOA_PRATICA.has(eixo) && !!focoInfo;
  const peso = focoInfo?.peso ?? 0;
  const qtdPacientes = focoInfo?.denominador ?? 0; // pacientes do indicador (denominador)
  const entraramPacientes = focoInfo?.numerador ?? 0;
  const pontuacaoMaxima = peso * qtdPacientes;
  const pontuacaoAtual = peso * entraramPacientes;
  const pontuacaoAConquistar = Math.max(pontuacaoMaxima - pontuacaoAtual, 0); // = peso * faltam
  const pctPontuacao = pontuacaoMaxima > 0 ? (pontuacaoAtual / pontuacaoMaxima) * 100 : 0;

  // Fetch equipes list for grouped mode.
  // Usa o endpoint por-eixo (mesmo do tab "Por Equipe"), que ja respeita a Unidade de Saude
  // selecionada e roteia PEC/DWH. Assim o modo agrupado lista apenas as equipes daquela unidade
  // (o generico /equipes nao filtra por unidade e mostrava todas as equipes do municipio).
  const fetchEquipes = useCallback(async () => {
    if (!municipioAtivo?.id || !eixo) return;
    try {
      const result = await saudeService.getEixoEquipes(eixo, municipioAtivo.id, qParam, unidadeSelecionada?.codunidade);
      setEquipesList((result || []).map((eq: any) => ({
        ine: eq.ine,
        equipe: eq.equipe || eq.nome,
        unidade: eq.unidade,
      })));
    } catch (err) { console.error('Erro ao carregar equipes:', err); }
    // `fonte` nao aparece no corpo: viaja pelo interceptor do services/api.ts, que a le do
    // sessionStorage. Precisa ficar nas deps para que alternar a fonte recarregue a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioAtivo?.id, eixo, qParam, unidadeSelecionada?.codunidade, fonte]);

  // Debounce da busca por paciente: espelha searchName em buscaDebounced apos 400ms sem digitar.
  useEffect(() => {
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(() => setBuscaDebounced(searchName), 400);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
  }, [searchName]);

  // Fetch data for flat mode or a specific equipe in grouped mode
  const fetchListaNominal = useCallback(async (ine?: string, pg = 1) => {
    if (!municipioAtivo?.id || !eixo) return null;
    const result = await saudeService.getListaNominal(
      eixo, municipioAtivo.id, pg, 50, ine || undefined, qParam, unidadeSelecionada?.codunidade,
      focusMode ? indicador : undefined, focusMode ? status : undefined, microAplicada || undefined,
      buscaAplicada || undefined,
    );
    return result;
    // `fonte` nao aparece no corpo: viaja pelo interceptor do services/api.ts, que a le do
    // sessionStorage. Precisa ficar nas deps para que alternar a fonte recarregue a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioAtivo?.id, eixo, qParam, unidadeSelecionada?.codunidade, focusMode, indicador, status, microAplicada, buscaAplicada, fonte]);

  // Reset da paginacao ao mudar o filtro de microarea ou a busca (evita pagina inexistente).
  useEffect(() => { setPage(1); }, [microAplicada, buscaAplicada]);

  // Flat mode fetch
  const fetchFlat = useCallback(async () => {
    if (!municipioAtivo?.id || !eixo) return;
    setLoading(true); setError('');
    try {
      const result = await fetchListaNominal(selectedIne, page);
      if (result) {
        setData(result.pacientes || []);
        setEixoNome(result.nome || eixo);
        setPaginacao(result.paginacao || { page: 1, limit: 50, total: 0, total_paginas: 0 });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar lista nominal');
      setData([]);
    } finally { setLoading(false); }
  }, [municipioAtivo?.id, eixo, page, selectedIne, fetchListaNominal]);

  useEffect(() => { if (!focusMode) fetchEquipes(); }, [fetchEquipes, focusMode]);
  useEffect(() => { if (!agrupado) fetchFlat(); }, [agrupado, fetchFlat]);

  // Modo foco: busca o resumo do eixo p/ obter nome da boa pratica + numerador/denominador
  // (usados nos rotulos Faltam/Entraram). Barato e ja cacheado no backend.
  useEffect(() => {
    if (!focusMode || !municipioAtivo?.id || !eixo) return;
    let cancel = false;
    (async () => {
      try {
        const resumo = await saudeService.getEixoResumo(eixo, municipioAtivo.id, undefined, qParam, unidadeSelecionada?.codunidade);
        const ind = (resumo?.indicadores || []).find((i: any) => (i.indicador || '').toUpperCase() === indicador);
        if (!cancel) setFocoInfo(ind ? { nome: ind.nome, numerador: ind.numerador || 0, denominador: ind.denominador || 0, peso: ind.peso || 0, tipo: resumo?.tipo } : null);
      } catch { if (!cancel) setFocoInfo(null); }
    })();
    return () => { cancel = true; };
    // `fonte` nao aparece no corpo: viaja pelo interceptor do services/api.ts, que a le do
    // sessionStorage. Precisa ficar nas deps para que alternar a fonte recarregue a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode, municipioAtivo?.id, eixo, indicador, qParam, unidadeSelecionada?.codunidade, fonte]);

  // Modo foco em eixo de TAXA (ex.: b2): faltam != den-num, entao busca os totais reais das duas
  // listas de foco (limit=1, so p/ ler paginacao.total). Barato e cacheado no backend.
  // Para eixos boas_praticas nao roda (mantem den-num / num do resumo).
  // Tambem NAO roda nos eixos por tipo de demanda (C1): ali a particao e exata por atendimento
  // (programados = numerador, espontaneos = den - num), entao as duas varreduras seriam desperdicio.
  useEffect(() => {
    if (!focusMode || focoInfo?.tipo !== 'taxa' || isFocoTipoDemanda(eixo) || !municipioAtivo?.id || !eixo) { setFocoCountsTaxa(null); return; }
    let cancel = false;
    (async () => {
      try {
        const [f, e] = await Promise.all([
          saudeService.getListaNominal(eixo, municipioAtivo.id, 1, 1, undefined, qParam, unidadeSelecionada?.codunidade, indicador, 'faltam'),
          saudeService.getListaNominal(eixo, municipioAtivo.id, 1, 1, undefined, qParam, unidadeSelecionada?.codunidade, indicador, 'entraram'),
        ]);
        if (!cancel) setFocoCountsTaxa({ faltam: f?.paginacao?.total ?? 0, entraram: e?.paginacao?.total ?? 0 });
      } catch { if (!cancel) setFocoCountsTaxa(null); }
    })();
    return () => { cancel = true; };
    // `fonte` nao aparece no corpo: viaja pelo interceptor do services/api.ts, que a le do
    // sessionStorage. Precisa ficar nas deps para que alternar a fonte recarregue a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode, focoInfo?.tipo, municipioAtivo?.id, eixo, indicador, qParam, unidadeSelecionada?.codunidade, fonte]);

  // When expanding an equipe accordion, lazy-load its data
  const handleAccordionExpand = async (ine: string) => {
    if (expandedIne === ine) { setExpandedIne(''); return; }
    setExpandedIne(ine);
    const eq = equipesList.find((e) => e.ine === ine);
    if (eq && !eq.pacientes) {
      setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: true } : e));
      try {
        const result = await fetchListaNominal(ine, 1);
        if (result) {
          setEquipesList((prev) => prev.map((e) =>
            e.ine === ine ? { ...e, pacientes: result.pacientes || [], paginacao: result.paginacao, loading: false, page: 1 } : e
          ));
        }
      } catch {
        setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: false } : e));
      }
    }
  };

  // Paginate within equipe accordion
  const handleEquipePage = async (ine: string, newPage: number) => {
    setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: true } : e));
    try {
      const result = await fetchListaNominal(ine, newPage);
      if (result) {
        setEquipesList((prev) => prev.map((e) =>
          e.ine === ine ? { ...e, pacientes: result.pacientes || [], paginacao: result.paginacao, loading: false, page: newPage } : e
        ));
      }
    } catch {
      setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: false } : e));
    }
  };

  // Auto-expand pre-selected equipe
  useEffect(() => {
    if (initialIne && agrupado && equipesList.length > 0) {
      handleAccordionExpand(initialIne);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipesList.length]);

  // Client-side filtering (search + somente incompletos) for flat mode
  const filteredData = data.filter((p) => {
    // Quando a busca e server-side (suportaBusca), o backend ja filtrou por nome/CPF/CNS em todo
    // o universo — nao reaplicar o filtro client-side (so por nome) para nao esconder matches de CPF/CNS.
    if (!suportaBusca && searchName && !p.no_cidadao?.toLowerCase().includes(searchName.toLowerCase())) return false;
    if (somenteIncompletos && indicators.length > 0) {
      const allMet = indicators.every((ind) => p[ind.key] === 1 || p[ind.key] === true);
      if (allMet) return false;
    }
    return true;
  });

  if (!municipioAtivo) {
    return (<Box sx={{ p: 3 }}><Alert severity="warning">Selecione um municipio.</Alert></Box>);
  }

  // Tabela do modo foco (boa pratica): colunas demograficas (endereco/telefone "—" no DWH).
  const renderFocusTable = (patients: Paciente[]) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>Nome</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Idade</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Area / Equipe</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Micro-area</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Endereco</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>CPF</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Telefone</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">{focoDataLabel(eixo)}</TableCell>
            {temProgresso && <TableCell sx={{ fontWeight: 600 }} align="center">Detalhe</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.length === 0 ? (
            <TableRow>
              <TableCell colSpan={temProgresso ? 9 : 8} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Nenhum cidadao encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            // Indice no key: nos eixos por tipo de demanda (C1) cada linha e um ATENDIMENTO, entao
            // o mesmo cidadao repete e o id sozinho geraria keys duplicadas.
            patients.map((p, idx) => (
              <TableRow key={`${p.co_seq_fat_cidadao_pec ?? 'sem-id'}-${idx}`} hover>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, maxWidth: 220 }}>
                  <Typography variant="body2" noWrap>{p.no_cidadao || '—'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={p.dt_nascimento ? `Nascimento: ${formatDataNascimento(p.dt_nascimento)}` : ''} arrow>
                    <span>{eixoEhCrianca ? formatIdadeAnosMeses(p.dt_nascimento) : (p.idade ?? '-')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{p.equipe || '—'}</Typography></TableCell>
                <TableCell align="center">{p.nu_micro_area || '—'}</TableCell>
                <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{p.endereco || '—'}</Typography></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{maskCpf(p.nu_cpf_cidadao)}</TableCell>
                <TableCell>{p.nu_telefone_celular || '—'}</TableCell>
                <TableCell align="center">{formatDate(p.dt_evento) || '—'}</TableCell>
                {temProgresso && (
                  <TableCell align="center">
                    <Tooltip title="Ver detalhe das boas praticas" arrow>
                      <IconButton size="small" onClick={() => setDetalhe(p)} sx={{ color: 'primary.main' }}>
                        <FactCheck sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPatientTable = (patients: Paciente[], showEquipeCol = true) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: indicators.length > 5 ? 1200 : 800 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>Nome</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>CPF</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Idade</TableCell>
            {showEquipeCol && <TableCell sx={{ fontWeight: 600 }}>Equipe</TableCell>}
            {indicators.map((ind) => {
              const helpKey = ind.key.replace('ind_', '').toUpperCase();
              return (
                <TableCell key={ind.key} sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="center">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                    {ind.label}
                    <HelpButton eixo={eixo!} indicador={helpKey} size={14} />
                  </Box>
                </TableCell>
              );
            })}
            <TableCell sx={{ fontWeight: 600 }} align="center">Score</TableCell>
            {temProgresso && <TableCell sx={{ fontWeight: 600 }} align="center">Detalhe</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4 + indicators.length + (showEquipeCol ? 1 : 0) + (temProgresso ? 1 : 0)} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Nenhum paciente encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            patients.map((paciente) => (
              <TableRow key={paciente.co_seq_fat_cidadao_pec} hover>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, maxWidth: 200 }}>
                  <Typography variant="body2" noWrap>{paciente.no_cidadao}</Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{maskCpf(paciente.nu_cpf_cidadao)}</TableCell>
                <TableCell>
                  <Tooltip title={paciente.dt_nascimento ? `Nascimento: ${formatDataNascimento(paciente.dt_nascimento)}` : ''} arrow>
                    <span>{eixoEhCrianca ? formatIdadeAnosMeses(paciente.dt_nascimento) : paciente.idade}</span>
                  </Tooltip>
                </TableCell>
                {showEquipeCol && (
                  <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{paciente.equipe}</Typography></TableCell>
                )}
                {indicators.map((ind) => {
                  // Boas praticas com progresso configurado: mostra a fracao (ex.: 5/9) inline.
                  const prog = progressoByKey[ind.key];
                  if (prog) {
                    const { atual, meta, concluido } = calcProgresso(paciente, prog);
                    const cor = concluido ? 'success.main' : atual > 0 ? 'warning.main' : 'text.disabled';
                    const resumo = prog.tipo === 'existencia' ? (concluido ? 'concluida' : 'pendente') : `${atual}/${meta}`;
                    return (
                      <TableCell key={ind.key} align="center">
                        <Tooltip title={`${prog.label}: ${resumo}`} arrow>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {prog.tipo === 'existencia' ? (
                              concluido
                                ? <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                                : <Remove sx={{ color: 'text.disabled', fontSize: 18 }} />
                            ) : (
                              <Typography variant="body2" fontWeight={700} sx={{ color: cor, fontSize: '0.8rem' }}>{atual}/{meta}</Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  const val = paciente[ind.key];
                  const isNumeric = typeof val === 'number' && val !== 0 && val !== 1;
                  const met = val === 1 || val === true || (typeof val === 'number' && val > 0);
                  const dateVal = ind.dateKey ? formatDate(paciente[ind.dateKey]) : '';
                  return (
                    <TableCell key={ind.key} align="center">
                      <Tooltip title={ind.label + (dateVal ? ` - ${dateVal}` : '') + (isNumeric ? `: ${val}` : '')} arrow>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                          {isNumeric ? (
                            <Typography variant="body2" fontWeight={600} sx={{ color: val >= 2 ? 'success.main' : 'warning.main' }}>{val}</Typography>
                          ) : met ? (
                            <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                          ) : (
                            <Remove sx={{ color: 'text.disabled', fontSize: 18 }} />
                          )}
                          {dateVal && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1 }}>{dateVal}</Typography>}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <Chip label={paciente.score_total ?? 0} size="small"
                    sx={{
                      fontWeight: 600, minWidth: 40,
                      bgcolor: paciente.score_total >= 75 ? '#e8f5e9' : paciente.score_total >= 50 ? '#fff3e0' : '#fce4ec',
                      color: paciente.score_total >= 75 ? '#2e7d32' : paciente.score_total >= 50 ? '#e65100' : '#c62828',
                    }} />
                </TableCell>
                {temProgresso && (
                  <TableCell align="center">
                    <Tooltip title="Ver detalhe das boas praticas" arrow>
                      <IconButton size="small" onClick={() => setDetalhe(paciente)} sx={{ color: 'primary.main' }}>
                        <FactCheck sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: focusMode ? 1.5 : 3, gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(`/saude/${eixo}`)} size="small"><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          {eixoNome || eixo} <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>- {focusMode ? focoTituloSufixo(eixo) : 'Lista Nominal'}</Typography>
        </Typography>
        {!focusMode && (
          <Button variant="outlined" startIcon={<FileDownload />} size="small"
            onClick={() => { if (municipioAtivo && eixo) saudeService.exportarCsv(eixo, municipioAtivo.id, selectedIne || undefined, qParam, unidadeSelecionada?.codunidade); }}>
            Exportar CSV
          </Button>
        )}
      </Box>

      {/* Toolbar do modo foco (boa pratica) */}
      {focusMode && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip label={indicador} color="primary" size="small" sx={{ fontWeight: 700 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 200 }}>
              {focoInfo?.nome || `Indicador ${indicador}`}
            </Typography>
            {mostrarPainelPontuacao && (
              <Chip label={`Peso ${peso}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
            <HelpButton eixo={eixo!} indicador={indicador} size={16} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup value={status} exclusive onChange={handleStatusChange} size="small" color="primary">
              <ToggleButton value="faltam" sx={{ textTransform: 'none', px: 2 }}>
                {focoLabels.faltam}{faltamCount != null ? ` (${faltamCount.toLocaleString('pt-BR')})` : ''}
              </ToggleButton>
              <ToggleButton value="entraram" sx={{ textTransform: 'none', px: 2 }}>
                {focoLabels.entraram}{entraramCount != null ? ` (${entraramCount.toLocaleString('pt-BR')})` : ''}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              Quadrimestre {qParam}{unidadeSelecionada?.nome ? ` · ${unidadeSelecionada.nome}` : ' · Todas as unidades'}
              {isFocoTipoDemanda(eixo) && ' · cada linha e um atendimento (o mesmo paciente pode aparecer mais de uma vez)'}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<FileDownload />} size="small"
                onClick={() => { if (municipioAtivo && eixo) saudeService.exportarCsv(eixo, municipioAtivo.id, undefined, qParam, unidadeSelecionada?.codunidade, indicador, status, microAplicada || undefined, buscaAplicada || undefined); }}>
                CSV
              </Button>
              <Button variant="outlined" startIcon={<PictureAsPdf />} size="small" color="error"
                onClick={() => { if (municipioAtivo && eixo) saudeService.exportarBoaPraticaPdf(eixo, municipioAtivo.id, indicador, status, qParam, unidadeSelecionada?.codunidade, microAplicada || undefined, buscaAplicada || undefined); }}>
                PDF
              </Button>
            </Box>
          </Box>
          {mostrarPainelPontuacao && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { rotulo: 'Pontuacao maxima', valor: pontuacaoMaxima, cor: 'text.primary', formula: `${peso} × ${qtdPacientes.toLocaleString('pt-BR')} pacientes` },
                  { rotulo: 'Pontuacao atual', valor: pontuacaoAtual, cor: 'success.main', formula: `${peso} × ${entraramPacientes.toLocaleString('pt-BR')} entraram` },
                  { rotulo: 'A conquistar', valor: pontuacaoAConquistar, cor: 'warning.main', formula: `${peso} × ${(qtdPacientes - entraramPacientes).toLocaleString('pt-BR')} faltam` },
                ].map((t) => (
                  <Box key={t.rotulo} sx={{ flex: '1 1 130px', minWidth: 120, px: 1, py: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', fontSize: '0.65rem' }}>
                      {t.rotulo}
                    </Typography>
                    <Typography fontWeight={700} sx={{ color: t.cor, lineHeight: 1.2, fontSize: '0.95rem' }}>
                      {t.valor.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {t.formula}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Pontuacao conquistada</Typography>
                  <Typography variant="caption" fontWeight={600} color="success.main" sx={{ fontSize: '0.65rem' }}>
                    {pctPontuacao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pctPontuacao, 100)}
                  color="success"
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Filters */}
      {focusMode ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder={suportaBusca ? 'Buscar por nome, CNS ou CPF...' : 'Buscar por nome...'} variant="outlined"
            value={searchName} onChange={(e) => setSearchName(e.target.value)} sx={{ minWidth: 260 }} />
          {suportaMicro && (
            <TextField size="small" label="Micro" placeholder="Filtra ao digitar 2 dígitos" variant="outlined"
              value={microInput}
              onChange={(e) => setMicroInput(e.target.value.replace(/\D/g, '').slice(0, 2))}
              inputProps={{ inputMode: 'numeric', maxLength: 2 }}
              sx={{ width: 210, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiInputBase-input::placeholder': { fontSize: '0.75rem' } }} />
          )}
          <Box sx={{ ml: 'auto' }}>
            {municipioAtivo && (
              <FonteDadosSelector municipioId={municipioAtivo.id} fonte={fonte} onChange={setFonte} quadrimestre={formatQuadrimestreParam(quadrimestre, ano)} />
            )}
            <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={agrupado} onChange={(e) => { setAgrupado(e.target.checked); if (!e.target.checked) fetchFlat(); }} size="small" />}
            label={<Typography variant="body2">Agrupar por equipe</Typography>}
          />

          {!agrupado && (
            <>
              <TextField size="small" placeholder="Buscar por nome..." variant="outlined"
                value={searchName} onChange={(e) => setSearchName(e.target.value)} sx={{ minWidth: 200 }} />
              {indicators.length > 0 && (
                <FormControlLabel
                  control={<Switch checked={somenteIncompletos} onChange={(e) => setSomenteIncompletos(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Somente pendentes</Typography>}
                />
              )}
            </>
          )}

          <Box sx={{ ml: 'auto' }}>
            {municipioAtivo && (
              <FonteDadosSelector municipioId={municipioAtivo.id} fonte={fonte} onChange={setFonte} quadrimestre={formatQuadrimestreParam(quadrimestre, ano)} />
            )}
            <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
          </Box>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Body */}
      {focusMode ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {renderFocusTable(filteredData)}
            {paginacao.total_paginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={paginacao.total_paginas} page={page} onChange={(_, v) => setPage(v)} color="primary" showFirstButton showLastButton />
              </Box>
            )}
          </>
        )
      ) : agrupado ? (
        equipesList.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          equipesList.map((eq) => (
            <Accordion
              key={eq.ine}
              expanded={expandedIne === eq.ine}
              onChange={() => handleAccordionExpand(eq.ine)}
              disableGutters
              sx={{
                mb: 1, borderRadius: '8px !important', overflow: 'hidden',
                border: '1px solid', borderColor: 'divider',
                '&:before': { display: 'none' }, boxShadow: 'none',
                '&.Mui-expanded': { margin: '0 0 8px 0 !important' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{eq.equipe}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{eq.unidade}</Typography>
                  </Box>
                  {eq.paginacao && (
                    <Chip label={`${eq.paginacao.total} pacientes`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {eq.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                ) : eq.pacientes ? (
                  <>
                    {renderPatientTable(eq.pacientes, false)}
                    {eq.paginacao && eq.paginacao.total_paginas > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <Pagination count={eq.paginacao.total_paginas} page={eq.page || 1}
                          onChange={(_, v) => handleEquipePage(eq.ine, v)} size="small" color="primary" />
                      </Box>
                    )}
                  </>
                ) : null}
              </AccordionDetails>
            </Accordion>
          ))
        )
      ) : (
        /* Flat mode */
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {renderPatientTable(filteredData)}
            {paginacao.total_paginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={paginacao.total_paginas} page={page} onChange={(_, v) => setPage(v)} color="primary" showFirstButton showLastButton />
              </Box>
            )}
          </>
        )
      )}

      {temProgresso && progresso && (
        <BoaPraticaProgressoDialog
          open={!!detalhe}
          onClose={() => setDetalhe(null)}
          eixo={eixo!}
          paciente={detalhe}
          descritores={progresso}
        />
      )}
    </Box>
  );
};

export default SaudeListaNominal;
